import type { MarketVariants } from '@/lib/i18n'

export type Slide4MasterKeyCopy = {
  /** Labels for the three services in the key diagram, top to bottom. */
  serviceLabels: string[]

  title: string
  body: string
  tipsButton: string

  /** Tips slideover. */
  guideTitle: string
  closeAria: string
  tip1Heading: string
  tip1Body: string
  exampleLabel: string
  tryAnother: string
  tryAnotherAria: string

  /** Interactive checker (currently hidden behind SHOW_PASSWORD_CHECKER). */
  checkLabel: string
  checkPlaceholder: string
  checkInputAria: string
  showPassword: string
  hidePassword: string
  /** zxcvbn score 0..4. */
  strengthLabels: string[]
  /** "{time}" is zxcvbn's English crack-time estimate, e.g. "3 hours". */
  crackTime: string
  privacyNote: string
  checking: string
  /** "{count}" is the formatted breach count. */
  foundOne: string
  foundMany: string
  foundNote: string
  notFound: string
  notFoundNote: string

  tip2Heading: string
  tip2Body: string
  tip3Heading: string
  // US canonical "organizations". Auto-converted to "organisations" on the Qatar face.
  tip3Body: string
  tip4Heading: string
  /** Tip 4 body split around the password-manager link: "{before}{link}{after}". */
  tip4Before: string
  tip4Link: string
  tip4After: string
  startEmailHeading: string
  startEmailBody: string
}

export const copy: MarketVariants<Slide4MasterKeyCopy> = {
  US: {
    serviceLabels: ['Email', 'Banking', 'Shopping'],

    title: 'Use a different password for every account.',
    body:
      'When a website gets hacked, a scammer walks away with a copy of your key. If that same key opens your bank and your email, they stroll straight in. A different key per lock keeps one copy from opening them all.',
    tipsButton: 'Password tips',

    guideTitle: 'Tips for Stronger Passwords',
    closeAria: 'Close',
    tip1Heading: '1. Make it long',
    tip1Body:
      'Three random words can make a strong password that is easier to remember.',
    exampleLabel: 'For example:',
    tryAnother: 'Try another',
    tryAnotherAria: 'Generate a new three-word example',

    checkLabel: 'Check your own:',
    checkPlaceholder: 'Type a password to check',
    checkInputAria: 'Password to check strength',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    strengthLabels: ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'],
    crackTime: '~{time} to crack',
    privacyNote:
      'Nothing is sent anywhere. Strength runs on your device. The breach check sends only a partial, hashed fingerprint, never your password.',
    checking: 'Checking known breaches…',
    foundOne: 'Found in {count} known breach',
    foundMany: 'Found in {count} known breaches',
    foundNote: 'Scammers already have this one. Pick a different one.',
    notFound: 'Not found in known breaches',
    notFoundNote: 'Still keep it unique to this account.',

    tip2Heading: 'Skip the personal details',
    tip2Body:
      'No birthdays, pet names, family names, or your hometown. Scammers can find all of this online.',
    tip3Heading: 'Never share them',
    tip3Body:
      'Not with family, not with "tech support," not with your bank on a phone call. Real organizations never ask for your password.',
    tip4Heading: 'You don’t have to remember them all yourself',
    tip4Before: 'Your phone, browser, or a ',
    tip4Link: 'password manager',
    tip4After:
      ' can save unique passwords for you automatically. And if you prefer pen and paper, a written list kept somewhere safe at home is a reasonable backup.',
    startEmailHeading: 'Start with your email password.',
    startEmailBody:
      'If a scammer gets into your email, they can reset the password on every other account you own. Protect it first.',
  },
  ar: {
    serviceLabels: ['البريد الإلكتروني', 'الخدمات المصرفية', 'التسوق'],

    title: 'استخدم كلمة مرور مختلفة لكل حساب.',
    body:
      'عندما يُخترق موقع إلكتروني، يحصل المحتال على نسخة من مفتاحك. وإذا كان المفتاح نفسه يفتح حسابك البنكي وبريدك الإلكتروني، فسيدخل إليهما بكل سهولة. أما مفتاح مختلف لكل قفل، فيمنع نسخة واحدة من فتحها جميعاً.',
    tipsButton: 'نصائح لكلمات المرور',

    guideTitle: 'نصائح لكلمات مرور أقوى',
    closeAria: 'إغلاق',
    tip1Heading: '1. اجعلها طويلة',
    tip1Body: 'ثلاث كلمات عشوائية تصنع كلمة مرور قوية ويسهل تذكّرها.',
    exampleLabel: 'مثال:',
    tryAnother: 'جرّب غيرها',
    tryAnotherAria: 'أنشئ مثالاً جديداً من ثلاث كلمات',

    checkLabel: 'افحص كلمة مرورك:',
    checkPlaceholder: 'اكتب كلمة مرور لفحصها',
    checkInputAria: 'كلمة المرور المراد فحص قوتها',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    strengthLabels: ['ضعيفة جداً', 'ضعيفة', 'مقبولة', 'قوية', 'قوية جداً'],
    crackTime: 'كسرها يستغرق نحو {time}',
    privacyNote:
      'لا يُرسل شيء إلى أي مكان. يُحسب مستوى القوة على جهازك. ويرسل فحص التسريبات بصمة جزئية مشفّرة فقط، وليس كلمة مرورك أبداً.',
    checking: 'جارٍ الفحص في التسريبات المعروفة…',
    foundOne: 'ظهرت في تسريب واحد معروف',
    foundMany: 'ظهرت في {count} من التسريبات المعروفة',
    foundNote: 'هذه الكلمة بحوزة المحتالين بالفعل. اختر كلمة أخرى.',
    notFound: 'لم تظهر في أي تسريب معروف',
    notFoundNote: 'ومع ذلك، اجعلها خاصة بهذا الحساب وحده.',

    tip2Heading: 'تجنّب التفاصيل الشخصية',
    tip2Body:
      'لا تواريخ ميلاد، ولا أسماء حيوانات أليفة، ولا أسماء عائلية، ولا اسم مدينتك. كل هذا يمكن للمحتالين إيجاده على الإنترنت.',
    tip3Heading: 'لا تشاركها أبداً',
    tip3Body:
      'لا مع العائلة، ولا مع "الدعم الفني"، ولا مع البنك في مكالمة هاتفية. الجهات الحقيقية لا تطلب كلمة مرورك أبداً.',
    tip4Heading: 'لست مضطراً لتذكّرها كلها بنفسك',
    tip4Before: 'يمكن لهاتفك أو متصفحك أو ',
    tip4Link: 'مدير كلمات المرور',
    tip4After:
      ' حفظ كلمات مرور فريدة لك تلقائياً. وإذا كنت تفضّل الورقة والقلم، فقائمة مكتوبة محفوظة في مكان آمن في المنزل خيار احتياطي معقول.',
    startEmailHeading: 'ابدأ بكلمة مرور بريدك الإلكتروني.',
    startEmailBody:
      'إذا دخل محتال إلى بريدك الإلكتروني، فيمكنه إعادة تعيين كلمة المرور لكل حساب آخر تملكه. احمِه أولاً.',
  },
}
